import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

export class TicketTableService extends InjectableClass {
  constructor(
    $filter,
    $q,
    $translate,
    $log,
    appCache,
    ticketsService,
    TicketTableFilterModifier,
    TicketStatesSiblings,
  ) {
    'ngInject';
    super({
      $filter,
      $q,
      $translate,
      $log,
      appCache,
      ticketsService,
      TicketTableFilterModifier,
      TicketStatesSiblings,
    });
  }

  get filterModifier() {
    return this.TicketTableFilterModifier;
  }

  isExplicitFilter(filter) {
    return angular.isObject(filter) && filter.explicit;
  }

  getTableStateKey(ticketType) {
    return `tickets${_.upperFirst(ticketType)}Table`;
  }

  getTableState(key) {
    // Ensure key is given (undefined will raise an error)
    return key && this.appCache.get(key);
  }

  setTableState(key, tableState) {
    this.appCache.put(key, tableState);
  }

  clearTableState(key) {
    this.appCache.remove(key);
  }

  initTableState(key) {
    // NOTE: This is a workaround for the issue that the table state
    //       is stored in the cache and not being updated.
    if (key) {
      this.clearTableState(key);
    }

    return {
      currentPage: 1,
      filter: {
        filterValues: [],
        filterType: { label: 'active', filter: { closed: false } },
        filterStatus: {},
      },
      pagination: { start: 0, number: 10, numberOfPages: 1 },
      search: {},
      sort: { predicate: 'created', reverse: false },
      predicate: 'created',
      reverse: false,
    };
  }

  getPage(ticketType, params, product = null) {
    const { pagination, filter, sort } = params;
    this.$log.debug('getPage', { ticketType, pagination, filter, sort });

    let pageQuery = this.getPageQuery(filter);

    // Create search filter from given params
    const searchFilter = ((config) => {
      /* remove all undefined values */
      for (let key in config) {
        if (angular.isUndefined(config[key])) {
          delete config[key];
        }
      }
      return config;
    })({
      textQuery:
        pageQuery.textQuery ||
        pageQuery.text /* this is actually a search term */,
      page:
        this.getPageNumber(pagination) ||
        1 /* just the page number. Defines which set of 20 tickts will be load. */,
      sortField:
        sort.predicate /* defines the sorting column-wise (clicking table column header) */,
      sortOrder:
        sort && sort.reverse ? 'ASC' : undefined /* sort order or direction */,
      whitelistStatus:
        pageQuery.whitelist /* i don't no. Something whitelisted. Apparently we can whitelist tickets. */,
      blacklistStatus:
        pageQuery.blacklist /* i don't no either. The opposite of whitelisting!? */,
      closed: pageQuery.closed /* means 'closed' or 'done' tickets. */,
      bankGroup:
        pageQuery.bankGroup /* see https://de.wikipedia.org/wiki/Bankleitzahl#Aufbau */,
      hasOpenStandingOrders: pageQuery.openStandingOrders,
    });

    return this.ticketsService.filterTickets(ticketType, searchFilter, product);
  }

  getPageQuery(filter = {}) {
    const query = {
      explicit: [],
      whitelist: [],
      blacklist: [],
    };

    // Handle live search value
    if (filter.liveSearch) {
      let liveSearch = this.filterLiveSearch(filter.liveSearch);
      (query.textQuery || (query.textQuery = [])).push(liveSearch);
    }

    // Handle filter values
    if (filter.filterValues) {
      const { TicketStatesSiblings } = this;
      filter.filterValues.map((value) => {
        // We treat any status filter value as whitelist filtering.
        // Since we translate the filtered status we have to use the key
        // instead of the value as query parameter.
        if (value.type === 'status') {
          const status = value.key;
          query.whitelist.push(status);

          // Add status siblings to whitelist as well
          if (TicketStatesSiblings[status]) {
            TicketStatesSiblings[status].map((sibling) => {
              query.whitelist.push(sibling);
            });
          }
        } else {
          (query[value.type] || (query[value.type] = [])).push(value.value);
        }
      });
    }

    // Handle filter type
    if (filter.filterType && filter.filterType.filter) {
      _.each(filter.filterType.filter, (val, key) => {
        (query[key] || (query[key] = [])).push(val);
      });
    }

    // Handle filter status
    if (filter.filterStatus) {
      const { filterStatus } = filter;

      // Treat all truthy filtered status as blacklist except
      // we have an explicit filtering declared.
      // Otherwise all falsy filter will be pushed to the whitelist.
      // ---
      // The reason why we need three different lists for status filtering:
      // We can't combine included (whitelist) and excluded (blacklist) status
      // to one status query because they are combined with an logical or operator `|`.
      // Therefore we need a separate handling for the blacklist and if we have
      // an explicit filtering, we will ignore the white- and blacklist completely.
      _.each(filterStatus, (filtered, status) => {
        if (filtered) {
          if (this.isExplicitFilter(filtered)) {
            query.explicit.push(status);
          } else {
            query.blacklist.push(status);
          }
        } else {
          query.whitelist.push(status);
        }
      });
    }

    return query;
  }

  getPageNumber(pagination = {}, defaultPage = 1) {
    const { number, numberOfPages, start } = pagination;
    if ((!number, !numberOfPages, !start)) {
      return defaultPage;
    }

    // important only consider first 12 decimals otherwise float precision will
    // calculate wrong number at one page eq 63.0000000000000000000001 will calculate 64 with Math.ceil
    return (
      Math.ceil(
        ((start / (number * numberOfPages)) * numberOfPages).toFixed(12),
      ) + 1
    );
  }

  filterLiveSearch(value) {
    // replace whitespaces in possible IBAN
    if ((value || '').match(/^[DE]{2}(?:[ ]?[0-9]){3,20}$/g)) {
      value = value.replaceAll(' ', '');
    }
    return (value || '').replace(/(,|<)/g, '');
  }

  applyFilterModifier(value, modifier, ticket) {
    // Translate ticket value
    if (modifier.translate) {
      let translateKey = `${modifier.translatePrefix}${value}`;
      if (modifier.translated[translateKey]) {
        value = modifier.translated[translateKey];
      } else {
        value = this.$translate.instant(translateKey);
        modifier.translated[translateKey] = value;
      }
    }

    // Change filter key to map ticket property
    if (modifier.key) {
      value = ticket[modifier.key];
    }

    // Modifier that depends on a given value
    if (value) {
      if (modifier.trim) {
        value = _.trim(value);
      }

      if (modifier.ignoreWhitespace) {
        value = value.replace(/\s+/, '');
      }
    }

    return value;
  }

  checkForTableState(key) {
    this.lockForPersist = this.getTableState(key);
  }

  unlockByPersist() {
    this.lockForPersist = false;
  }
}
